/**
 * MonsterDataNormalizer - Ensures consistent monster data structure
 *
 * This utility normalizes monster data from various sources (JSON schema, legacy format, custom monsters)
 * into a unified structure that all components can rely on.
 *
 * date_of_change: 2025-09-23
 * - Align extraction with modern monster template (frontend/src/schemas/monster_template.json):
 *   - Prefer data.defense.hp.{current,max} and data.defense.ac.base when present
 *   - Prefer data.movement.walk (string like "30 ft") for speed; parse numeric feet
 *   - Prefer data.taxonomy.{size,type,alignment} for identity
 *   - Prefer data.proficiencies.{savingThrows,skills} for bonuses
 *   - Prefer data.languages.spoken array for languages
 * - Preserve backward-compat chains for legacy fields (armorClass/hitPoints/etc.)
 * - Add safe helpers to parse distances and title-case taxonomy size
 * rule_reference: Character_Monster_Architecture_Validation_Plan.md (Phase 3) — Zero transformation beyond ingestion; preserve compat on input, emit consistent runtime fields.
 */
import HPFormatBridge from './HPFormatBridge';
import MonsterDataCache from './MonsterDataCache';

export class MonsterDataNormalizer {
  /**
   * Normalize monster data to consistent format
   * @param {Object} rawData - Raw monster data from any source
   * @param {boolean} [useCache=true] - Whether to use the cache system
   * @returns {Object} - Normalized monster data
   */
  /**
   * Normalize monster data to consistent format
   * 
   * @date_of_change: 2025-08-23
   * - Added preservation logic for existing values
   * - Added comprehensive debug logging
   * - Enhanced field access with fallback chains
   * @date_of_change: 2025-08-25
   * - Added cache integration for performance optimization
   * - Added support for versioned caching through schemaVersion
   */
  static normalize(rawData, useCache = true) {
    // Debug start of normalization process with raw data summary
    console.debug('[DEBUG:MonsterDataNormalizer] Starting normalization', {
      id: rawData?.id,
      name: rawData?.name || rawData?.data?.name,
      rawDataKeys: rawData ? Object.keys(rawData) : 'null',
      hasNestedData: Boolean(rawData?.data)
    });
    
    if (!rawData) {
      return this.getDefaultMonster();
    }

    // Check if we can use the cache
    if (useCache && rawData.id) {
      // Attempt to load from cache first if we have an ID
      // Use schema version for versioned caching if available
      const cachedMonster = MonsterDataCache.get(rawData.id, rawData.schemaVersion);
      if (cachedMonster) {
        console.debug(`[MonsterDataNormalizer] Cache hit for ${rawData.id}`);
        return cachedMonster;
      }
    }

    // Extract stats from various possible formats
    const stats = this.extractStats(rawData);
    
    // Preserve original values for preservation logic
    const preservationData = {
      _preserveHp: rawData.hp,
      _preserveMaxHp: rawData.maxHp,
      _preserveAc: rawData.ac
    };
    
    // Extract basic properties
    const normalized = {
      // Core identification
      id: rawData.id || rawData.templateId || rawData.data?.id || 'unknown',
      name: rawData.name || rawData.data?.name || 'Unknown Monster',
      type: 'monster',
      
      // Combat stats
      cr: this.extractCR(rawData),
      hp: this.extractHP({...rawData, ...preservationData}),
      maxHp: this.extractMaxHP({...rawData, ...preservationData}),
      hpObj: this.createHPObject(rawData),
      ac: this.extractAC({...rawData, ...preservationData}),
      speed: this.extractSpeed(rawData),
      initiative: rawData.initiative || Math.floor((stats.dex - 10) / 2),
      // Preserve original xp value if available
      xp: rawData.xp || rawData.data?.challenge?.xp || rawData.data?.xp || this.calculateXPFromCR(this.extractCR(rawData)),
      
      // Physical properties
      // date_of_change: 2025-09-23 - Prefer taxonomy block in modern schema
      size: this.extractSize(rawData),
      monsterType: this.extractMonsterType(rawData),
      alignment: this.extractAlignment(rawData),
      
      // Ability scores in both formats for compatibility
      stats: { ...stats }, // Create a copy of stats object for backwards compatibility
      abilities: {
        strength: stats.str,
        dexterity: stats.dex,
        constitution: stats.con,
        intelligence: stats.int,
        wisdom: stats.wis,
        charisma: stats.cha
      },
      
      // Combat modifiers
      skills: this.extractSkills(rawData),
      savingThrows: this.extractSavingThrows(rawData),
      ...(() => {
        const mods = this.extractDamageModifiers(rawData);
        return {
          damageResistances: mods.resistances,
          damageImmunities: mods.immunities,
          damageVulnerabilities: mods.vulnerabilities
        };
      })(),
      conditionImmunities: rawData.conditionImmunities || rawData.data?.defense?.conditionImmunities || rawData.data?.conditionImmunities || [],
      
      // Senses and communication
      senses: this.extractSenses(rawData),
      languages: this.extractLanguages(rawData),
      
      // Actions and abilities
      actions: this.extractActions(rawData.actions || rawData.data?.actions),
      // Bonus actions and reactions are first-class canonical buckets. Keep
      // them discrete during intake so their costs survive persistence and
      // BattleRoom projection.
      // rule_reference: D&D 5e action economy; date_of_change: 2026-09-02
      bonusActions: this.extractActions(rawData.bonusActions || rawData.data?.bonusActions),
      specialAbilities: this.extractActions(rawData.specialAbilities || rawData.traits || rawData.data?.traits),
      traits: this.extractActions(rawData.traits || rawData.specialAbilities || rawData.data?.traits),
      legendaryActions: this.extractActions(rawData.legendaryActions || rawData.data?.legendaryActions),
      reactions: this.extractActions(rawData.reactions || rawData.data?.reactions),
      
      // Artwork
      artwork: rawData.artwork || rawData.data?.images?.artwork || rawData.image || '/assets/monsters/default-monster.png',
      token: rawData.token || rawData.data?.images?.token || '/assets/monsters/tokens/default-token.png',
      
      // Lore data
      lore: this.extractLore(rawData),
      
      // Metadata
      isCustom: rawData.isCustom || false,
      source: rawData.source || { name: 'Unknown', edition: '2024 Core' },
      
      // Schema tracking for future migrations
      schemaVersion: rawData.schemaVersion || '1.0'
    };

    // Preserve any custom fields that aren't part of our standard schema
    // This ensures user-defined properties survive normalization
    if (rawData) {
      const preservedCustomFields = {};
      const standardFields = [
        'id', 'name', 'type', 'cr', 'hp', 'maxHp', 'hpObj', 'ac', 'speed',
        'initiative', 'size', 'monsterType', 'alignment', 'stats', 'abilities',
        'skills', 'savingThrows', 'damageResistances', 'damageImmunities',
        'damageVulnerabilities', 'conditionImmunities', 'senses', 'languages',
        'actions', 'bonusActions', 'specialAbilities', 'traits', 'legendaryActions', 'reactions',
        'artwork', 'token', 'lore', 'isCustom', 'source', 'schemaVersion'
      ];
      
      Object.keys(rawData).forEach(key => {
        if (!standardFields.includes(key) && typeof rawData[key] !== 'function') {
          preservedCustomFields[key] = rawData[key];
          console.debug(`[DEBUG:MonsterDataNormalizer] Preserving custom field: ${key}`); 
        }
      });
      
      // Apply preserved custom fields
      Object.assign(normalized, preservedCustomFields);
    }
    
    // Validate the normalized data
    this.validate(normalized);
    
    // Debug end of normalization process
    console.debug('[DEBUG:MonsterDataNormalizer] Normalization complete', {
      id: normalized.id,
      name: normalized.name,
      normalizedKeys: Object.keys(normalized),
      crSource: normalized.cr,
      hpSource: normalized.hp,
      acSource: normalized.ac
    });
    
    // Store in cache if we have a valid ID and caching is enabled
    if (useCache && normalized.id && normalized.id !== 'unknown') {
      MonsterDataCache.set(normalized.id, normalized, normalized.schemaVersion);
    }
    
    return normalized;
  }

  /**
   * Extract ability scores from various formats
   * 
   * date_of_change: 2025-09-02
   * - Reordered to prioritize standard schema format (data.abilities with long names)
   * - Fixed monster ability scores showing defaults by checking long names first
   */
  static extractStats(data) {
    console.debug('[DEBUG:MonsterDataNormalizer] Extracting stats from:', {
      hasStats: !!data.stats,
      hasAbilities: !!data.abilities,
      hasDataAbilities: !!data.data?.abilities,
      statsKeys: data.stats ? Object.keys(data.stats) : null,
      abilitiesKeys: data.abilities ? Object.keys(data.abilities) : null,
      dataAbilitiesKeys: data.data?.abilities ? Object.keys(data.data.abilities) : null
    });
    
    // Try standard schema format first (data.abilities with long names)
    if (data.data?.abilities && typeof data.data.abilities === 'object') {
      // Check for long names (standard monster schema format)
      if (data.data.abilities.strength !== undefined) {
        const result = {
          str: data.data.abilities.strength || 10,
          dex: data.data.abilities.dexterity || 10,
          con: data.data.abilities.constitution || 10,
          int: data.data.abilities.intelligence || 10,
          wis: data.data.abilities.wisdom || 10,
          cha: data.data.abilities.charisma || 10
        };
        console.debug('[DEBUG:MonsterDataNormalizer] Extracted stats from data.abilities (long names):', result);
        return result;
      }
      // Check for short names in data.abilities
      if (data.data.abilities.str !== undefined) {
        const result = {
          str: data.data.abilities.str || 10,
          dex: data.data.abilities.dex || 10,
          con: data.data.abilities.con || 10,
          int: data.data.abilities.int || 10,
          wis: data.data.abilities.wis || 10,
          cha: data.data.abilities.cha || 10
        };
        console.debug('[DEBUG:MonsterDataNormalizer] Extracted stats from data.abilities (short names):', result);
        return result;
      }
    }
    
    // Try direct abilities format with long names
    if (data.abilities && typeof data.abilities === 'object' && data.abilities.strength !== undefined) {
      const result = {
        str: data.abilities.strength || 10,
        dex: data.abilities.dexterity || 10,
        con: data.abilities.constitution || 10,
        int: data.abilities.intelligence || 10,
        wis: data.abilities.wisdom || 10,
        cha: data.abilities.charisma || 10
      };
      console.debug('[DEBUG:MonsterDataNormalizer] Extracted stats from abilities (long names):', result);
      return result;
    }
    
    // Try direct abilities format with short names
    if (data.abilities && typeof data.abilities === 'object' && data.abilities.str !== undefined) {
      const result = {
        str: data.abilities.str || 10,
        dex: data.abilities.dex || 10,
        con: data.abilities.con || 10,
        int: data.abilities.int || 10,
        wis: data.abilities.wis || 10,
        cha: data.abilities.cha || 10
      };
      console.debug('[DEBUG:MonsterDataNormalizer] Extracted stats from abilities (short names):', result);
      return result;
    }
    
    // Try stats format (preferred for legacy data)
    if (data.stats && typeof data.stats === 'object' && data.stats.str !== undefined) {
      const result = {
        str: data.stats.str || 10,
        dex: data.stats.dex || 10,
        con: data.stats.con || 10,
        int: data.stats.int || 10,
        wis: data.stats.wis || 10,
        cha: data.stats.cha || 10
      };
      console.debug('[DEBUG:MonsterDataNormalizer] Extracted stats from stats:', result);
      return result;
    }
    
    // Try full name format at root level
    if (data.strength !== undefined || data.dexterity !== undefined) {
      const result = {
        str: data.strength || 10,
        dex: data.dexterity || 10,
        con: data.constitution || 10,
        int: data.intelligence || 10,
        wis: data.wisdom || 10,
        cha: data.charisma || 10
      };
      console.debug('[DEBUG:MonsterDataNormalizer] Extracted stats from root level (long names):', result);
      return result;
    }
    
    // Default stats
    console.debug('[DEBUG:MonsterDataNormalizer] Using default stats (no ability scores found)');
    return { str: 10, dex: 10, con: 10, int: 10, wis: 10, cha: 10 };
  }

  /**
   * Extract Challenge Rating from various formats
   * 
   * @date_of_change 2025-08-23
   * - Enhanced with comprehensive fallback chains for all possible CR formats
   * - Added detailed debug logging for CR field access to identify data loss
   */
  static extractCR(data) {
    // Log all possible sources for CR value to help debug data loss
    console.debug('[DEBUG:DATA_LOSS] CR sources:', {
      cr: data.cr,
      challenge_rating: data.challenge_rating,
      challengeRating: data.challengeRating,
      dataCr: data.data?.cr,
      dataChallengeCr: data.data?.challenge?.cr,
      crValue: data?.cr?.value,
      crDisplay: data?.cr?.display
    });
    
    // Enhanced fallback chain to handle all possible formats
    return (
      // Modern schema challenge.cr (number or string)
      (data.data?.challenge?.cr !== undefined && String(data.data.challenge.cr)) ||
      // Direct string CR
      (typeof data.cr === 'string' && data.cr) ||
      // Direct number CR
      (typeof data.cr === 'number' && String(data.cr)) ||
      // Structured CR object with display property
      data.cr?.display ||
      // Structured CR object with value property
      (data.cr?.value !== undefined && String(data.cr.value)) ||
      // Alternative property names
      data.challenge_rating ||
      data.challengeRating ||
      // Nested in data object (JSON schema format)
      data.data?.cr?.display ||
      (data.data?.cr?.value !== undefined && String(data.data.cr.value)) ||
      data.data?.challenge_rating ||
      data.data?.challengeRating ||
      // Default fallback
      '1/4'
    );
  }

  /**
   * Extract Hit Points from various formats
   * 
   * date_of_change: 2025-08-23
   * - Enhanced with comprehensive fallback chain for all HP formats
   * - Added detailed debug logging for HP field access to identify data loss
   * - Preserves existing HP values when available
   */
  static extractHP(data) {
    // Log all possible sources for HP value to help debug data loss
    console.debug('[DEBUG:DATA_LOSS] HP sources:', {
      hp: data.hp,
      hitPoints: data.hitPoints,
      hit_points: data.hit_points,
      dataHp: data.data?.hp,
      defenseHp: data.data?.defense?.hp,
      current: data.hpObj?.current,
      hpAverage: data.hp?.average,
      hpValue: data.hp?.value
    });
    
    // Enhanced fallback chain with preservation logic
    return (
      // Preserve existing HP value if already set
      (typeof data._preserveHp === 'number' && data._preserveHp) ||
      // Modern schema: data.defense.hp.current or data.defense.hp.max (if current missing)
      (typeof data.data?.defense?.hp?.current === 'number' && data.data.defense.hp.current) ||
      (typeof data.data?.defense?.hp?.max === 'number' && data.data.defense.hp.max) ||
      
      // Standard number format
      (typeof data.hp === 'number' && data.hp) ||
      
      // Unified HP object format
      (typeof data.hpObj?.current === 'number' && data.hpObj.current) ||
      
      // EntityMigrator format: {average, formula}
      data.hp?.average ||
      
      // Legacy format with value property
      data.hp?.value ||
      data.hp?.current ||
      
      // Alternative property names
      data.hitPoints ||
      data.hit_points ||
      
      // Nested formats in data object
      data.data?.hp?.average ||
      data.data?.hp?.value ||
      data.data?.hp?.current ||
      data.data?.hitPoints ||
      data.data?.hit_points ||
      
      // Default fallback
      10
    );
  }

  /**
   * Extract Max Hit Points
   * 
   * date_of_change: 2025-08-23
   * - Enhanced with comprehensive fallback chain for all maxHP formats
   * - Added detailed debug logging for maxHP field access
   */
  static extractMaxHP(data) {
    // Log all possible sources for maxHP value
    console.debug('[DEBUG:DATA_LOSS] maxHP sources:', {
      maxHp: data.maxHp,
      maxHitPoints: data.maxHitPoints,
      max_hit_points: data.max_hit_points,
      hpMax: data.hp?.max,
      hpObjMax: data.hpObj?.max,
      dataHpMax: data.data?.hp?.max,
      defenseHpMax: data.data?.defense?.hp?.max
    });
    
    // Enhanced fallback chain
    const maxHp = (
      // Preserve existing maxHP value if already set
      (typeof data._preserveMaxHp === 'number' && data._preserveMaxHp) ||
      // Modern schema
      data.data?.defense?.hp?.max ||
      
      // Standard property
      data.maxHp ||
      
      // Unified HP object format
      data.hpObj?.max ||
      
      // HP object structure
      data.hp?.max ||
      
      // Alternative property names
      data.maxHitPoints ||
      data.max_hit_points ||
      
      // Nested formats in data object
      data.data?.hp?.max ||
      data.data?.maxHp ||
      data.data?.maxHitPoints ||
      data.data?.max_hit_points ||
      
      // Use current HP as fallback
      this.extractHP(data)
    );
    
    return maxHp;
  }
  
  /**
   * Create or extract an HP object with current and max properties
   * This is the preferred format for HP representation
   * 
   * date_of_change: 2025-08-22
   * - Updated to use HPFormatBridge for comprehensive format handling
   * - Adds support for EntityMigrator format (hp: {average, formula})
   * - Ensures consistent HP representation across all data sources
   */
  static createHPObject(data) {
    // Use HPFormatBridge for comprehensive format handling
    // This handles all formats including EntityMigrator format
    const unifiedHP = HPFormatBridge.createUnifiedHP(data);
    
    // If the bridge provided a valid HP object, use it
    if (unifiedHP && typeof unifiedHP.current === 'number') {
      return unifiedHP;
    }
    
    // Fallback to original implementation if bridge fails
    // If data already has a properly formatted hpObj, use it
    if (data.hpObj && typeof data.hpObj === 'object' && 
        'current' in data.hpObj && 'max' in data.hpObj) {
      return data.hpObj;
    }
    
    // Get current and max HP values from various possible formats
    const current =
      (typeof data.data?.defense?.hp?.current === 'number' ? data.data.defense.hp.current : undefined) ??
      data.hp ?? data.hitPoints ?? data.hp?.current ?? data.hpObj?.current ?? this.extractHP(data);
                   
    const max =
      (typeof data.data?.defense?.hp?.max === 'number' ? data.data.defense.hp.max : undefined) ??
      data.maxHp ?? data.maxHitPoints ?? data.hp?.max ?? data.hpObj?.max ?? this.extractMaxHP(data);
    
    return {
      current: typeof current === 'number' ? current : parseInt(current) || 0,
      max: typeof max === 'number' ? max : parseInt(max) || 0
    };
  }

  /**
   * Extract Armor Class from various formats
   * 
   * date_of_change: 2025-08-23
   * - Enhanced with comprehensive fallback chain for all AC formats
   * - Added detailed debug logging for AC field access
   */
  static extractAC(data) {
    // Log all possible sources for AC value
    console.debug('[DEBUG:DATA_LOSS] AC sources:', {
      ac: data.ac,
      armorClass: data.armorClass,
      armor_class: data.armor_class,
      dataAc: data.data?.ac,
      defenseAc: data.data?.defense?.ac,
      acValue: data.ac?.value
    });
    
    // Enhanced fallback chain
    return (
      // Preserve existing AC value if already set
      (typeof data._preserveAc === 'number' && data._preserveAc) ||
      // Modern schema base AC
      (typeof data.data?.defense?.ac?.base === 'number' && data.data.defense.ac.base) ||
      
      // Standard number format
      (typeof data.ac === 'number' && data.ac) ||
      
      // Object with value property
      data.ac?.value ||
      
      // Alternative property names
      data.armorClass ||
      data.armor_class ||
      
      // Nested in data object
      data.data?.ac?.value ||
      data.data?.armorClass ||
      data.data?.armor_class ||
      
      // Default fallback
      10
    );
  }

  /**
   * Extract speed from various formats
   */
  static extractSpeed(data) {
    // date_of_change: 2025-09-23 - Prefer modern movement.walk, parse numeric feet from strings
    const parseDistanceNumber = (val) => {
      if (typeof val === 'number') return val;
      if (typeof val === 'string') {
        const m = val.match(/\d+/);
        return m ? parseInt(m[0], 10) : undefined;
      }
      return undefined;
    };
    if (typeof data.data?.movement?.walk !== 'undefined') {
      const v = parseDistanceNumber(data.data.movement.walk);
      if (typeof v === 'number') return v;
    }
    if (typeof data.speed === 'number') return data.speed;
    if (typeof data.speed?.walk !== 'undefined') {
      const v = parseDistanceNumber(data.speed.walk);
      if (typeof v === 'number') return v;
    }
    if (typeof data.data?.speed?.walk !== 'undefined') {
      const v = parseDistanceNumber(data.data.speed.walk);
      if (typeof v === 'number') return v;
    }
    return 30;
  }

  /**
   * Extract monster type from various formats
   * 
   * date_of_change: 2025-08-23
   * - Enhanced with comprehensive fallback chain for all monster type formats
   * - Added detailed debug logging for type field access
   */
  static extractMonsterType(data) {
    // Log all possible sources for monster type
    console.debug('[DEBUG:DATA_LOSS] Monster type sources:', {
      monsterType: data.monsterType,
      creature_type: data.creature_type,
      creatureType: data.creatureType,
      type: data.type,
      taxonomy: data.data?.taxonomy,
      dataCreatureType: data.data?.creatureType,
      dataType: data.data?.type
    });
    
    // Enhanced fallback chain
    return (
      // Modern taxonomy.type
      data.data?.taxonomy?.type ||
      // Direct property
      data.monsterType ||
      
      // Structured type object
      data.creatureType?.type ||
      data.creature_type?.type ||
      
      // Nested in data object
      data.data?.creatureType?.type ||
      data.data?.creature_type?.type ||
      
      // Type field (when not 'monster' which indicates entity type, not creature type)
      (data.type && data.type !== 'monster' && data.type) ||
      (data.data?.type && data.data.type !== 'monster' && data.data.type) ||
      
      // Default fallback
      'humanoid'
    );
  }

  /**
   * Extract size from modern taxonomy or legacy fields
   * date_of_change: 2025-09-23
   */
  static extractSize(data) {
    const raw = data.data?.taxonomy?.size || data.size || data.data?.size || 'Medium';
    if (typeof raw !== 'string') return 'Medium';
    // Title-case common size strings
    const lower = raw.toLowerCase();
    const map = { tiny: 'Tiny', small: 'Small', medium: 'Medium', large: 'Large', huge: 'Huge', gargantuan: 'Gargantuan' };
    return map[lower] || (raw.charAt(0).toUpperCase() + raw.slice(1));
  }

  /**
   * Extract alignment from modern taxonomy or legacy fields
   * date_of_change: 2025-09-23
   */
  static extractAlignment(data) {
    return data.data?.taxonomy?.alignment || data.alignment || data.data?.alignment || 'neutral';
  }

  /**
   * Extract skills from various formats
   */
  static extractSkills(data) {
    // date_of_change: 2025-09-23 - Prefer modern data.proficiencies.skills numbers
    const profSkills = data.data?.proficiencies?.skills;
    const skills = profSkills || data.skills || data.data?.skills || {};
    const normalized = {};
    
    for (const [skill, value] of Object.entries(skills)) {
      if (typeof value === 'object' && value.bonus !== undefined) {
        normalized[skill] = value.bonus;
      } else if (typeof value === 'number') {
        normalized[skill] = value;
      }
    }
    
    return normalized;
  }

  /**
   * Extract saving throws from various formats
   */
  static extractSavingThrows(data) {
    // date_of_change: 2025-09-23 - Prefer modern data.proficiencies.savingThrows
    // Canonicalize keys to 3-letter abbreviations: str, dex, con, int, wis, cha
    const toAbbr = (obj) => {
      if (!obj || typeof obj !== 'object') return {};
      const map = {
        strength: 'str', dexterity: 'dex', constitution: 'con',
        intelligence: 'int', wisdom: 'wis', charisma: 'cha'
      };
      const out = {};
      for (const [k, v] of Object.entries(obj)) {
        if (typeof v !== 'number') continue;
        const key = map[k] || k; // already abbreviated or unknown
        out[key] = v;
      }
      return out;
    };

    const modern = data.data?.proficiencies?.savingThrows;
    if (modern && typeof modern === 'object') {
      return toAbbr(modern);
    }

    const legacy = data.savingThrows || data.saves || data.data?.saves || {};
    return toAbbr(legacy);
  }

  /**
   * Extract damage list (resistances, immunities, vulnerabilities)
   */
  static extractDamageList(damageList) {
    if (!damageList) return [];
    if (Array.isArray(damageList)) {
      return damageList.map(item => typeof item === 'object' ? item.type : item);
    }
    if (typeof damageList === 'string') {
      return damageList.split(',').map(item => item.trim());
    }
    return [];
  }

  /**
   * date_of_change: 2025-09-23
   * Extract and split modern damage modifiers from data.defense.damageModifiers
   * into simple arrays of strings for resistances, immunities, vulnerabilities.
   * Falls back to legacy fields when modern structure is absent.
   */
  static extractDamageModifiers(data) {
    const result = { resistances: [], immunities: [], vulnerabilities: [] };
    const mods = data.data?.defense?.damageModifiers;
    if (Array.isArray(mods)) {
      mods.forEach(m => {
        if (!m || typeof m !== 'object') return;
        const type = m.damageType || m.type;
        const kind = (m.kind || '').toLowerCase();
        if (!type) return;
        if (kind === 'resistant') result.resistances.push(type);
        else if (kind === 'immune') result.immunities.push(type);
        else if (kind === 'vulnerable') result.vulnerabilities.push(type);
      });
      return result;
    }
    // Legacy fallbacks
    result.resistances = this.extractDamageList(data.damageResistances || data.data?.damageModifiers?.resistances);
    result.immunities = this.extractDamageList(data.damageImmunities || data.data?.damageModifiers?.immunities);
    result.vulnerabilities = this.extractDamageList(data.damageVulnerabilities || data.data?.damageModifiers?.vulnerabilities);
    return result;
  }

  /**
   * Extract senses from various formats
   */
  static extractSenses(data) {
    if (data.senses && typeof data.senses === 'string') return data.senses;
    if (data.data?.senses?.notes) return data.data.senses.notes;
    
    // Build senses string from components
    const senses = [];
    const senseData = data.senses || data.data?.senses || {};
    const unifyFeet = (val) => {
      if (typeof val === 'number') return `${val} ft.`;
      if (typeof val === 'string') {
        const trimmed = val.trim();
        return /ft\.?$/.test(trimmed) ? trimmed : `${trimmed} ft.`;
      }
      return undefined;
    };
    
    if (senseData.darkvision) senses.push(`darkvision ${unifyFeet(senseData.darkvision)}`);
    if (senseData.blindsight) senses.push(`blindsight ${unifyFeet(senseData.blindsight)}`);
    if (senseData.tremorsense) senses.push(`tremorsense ${unifyFeet(senseData.tremorsense)}`);
    if (senseData.truesight) senses.push(`truesight ${unifyFeet(senseData.truesight)}`);
    
    const passivePerception = senseData.passivePerception || 10;
    senses.push(`passive Perception ${passivePerception}`);
    
    return senses.join(', ');
  }

  /**
   * Extract languages from various formats
   */
  static extractLanguages(data) {
    // date_of_change: 2025-09-23 - Prefer data.languages.spoken array, include telepathy when present
    const languages = data.languages || data.data?.languages;
    const modern = data.data?.languages;
    
    if (Array.isArray(languages)) return languages.join(', ');
    if (languages?.list && Array.isArray(languages.list)) return languages.list.join(', ');
    if (modern?.spoken && Array.isArray(modern.spoken)) {
      const base = modern.spoken.join(', ');
      const tel = modern.telepathy ? `; telepathy ${modern.telepathy}` : '';
      return (base + tel).trim();
    }
    if (typeof languages === 'string') return languages;
    
    return 'Common';
  }

  /**
   * Extract actions/abilities from various formats and normalize into consistent structure
   * with discrete fields for all attack properties
   * 
   * @param {Array} actions - Array of action objects from any source format
   * @returns {Array} Normalized action objects with consistent structure
   * 
   * @example
   * // Input: [{name: "Longsword", damage: "1d8+3", damageType: "slashing"}]
   * // Output: [{name: "Longsword", damage: {dice: "1d8", bonus: 3}, damageTypes: [{type: "slashing", dice: "1d8+3"}], ...}]
   */
  static extractActions(actions) {
    // DEBUG
    console.debug('[MonsterDataNormalizer] Extracting actions from:', actions);
    
    if (!actions || !Array.isArray(actions)) return [];
    
    return actions.map(action => {
      // Extract basic properties
      const name = action.name || 'Unnamed Action';
      let description = action.description || action.text || '';
      
      // Initialize structured attack properties
      let attackType = action.type || 'melee';
      let attackBonus = action.attackBonus || action.attack?.toHit || this.extractAttackBonus(action) || 0;
      let reachOrRange = this.extractReachRange(action);
      let damageEntries = this.extractDamageEntries(action);
      let savingThrow = this.extractSavingThrow(action);
      let targets = action.targets || action.attack?.targets || 'one target';
      let additionalEffects = action.effect || action.notes || action.attack?.riders || '';
      
      // Normalize special hyphens/dashes in description for parsing (keep original description for display)
      const parsedText = typeof description === 'string' 
        ? description.replace(/[\u2010-\u2015\u2212]/g, '-') 
        : '';

      // Prefer explicit area field if present
      let areaInfo = null;
      if (action.area && (action.area.shape || action.area.type) && (action.area.size || action.area.radius)) {
        const shape = (action.area.shape || action.area.type || '').toString().toLowerCase();
        const sizeVal = Number(action.area.size ?? action.area.radius);
        areaInfo = {
          type: shape || null,
          size: isNaN(sizeVal) ? null : sizeVal,
          unit: (action.area.unit || 'ft'),
          targets: null
        };
      } else {
        areaInfo = this.parseAreaEffectFromDescription(parsedText);
      }

      // date_of_change: 2025-08-25
      // Prefer explicit recharge field if present; else parse from text.
      // Accept multiple shapes used across sources:
      // - recharge: "5-6" | 6
      // - recharge: { value|roll|text|range|on: "5-6" | 6, min, max }
      // - recharge: { type: 'rechargeRoll', value: '5-6' }
      let recharge = null;
      if (action.recharge) {
        const raw = action.recharge;
        const val = (typeof raw === 'object')
          ? (raw.value ?? raw.roll ?? raw.text ?? raw.range ?? raw.on)
          : raw; // allow string/number directly
        const min = typeof raw === 'object' ? raw.min : undefined;
        const max = typeof raw === 'object' ? raw.max : undefined;
        if (typeof min === 'number' || typeof max === 'number') {
          const minNum = typeof min === 'number' ? min : max;
          const maxNum = typeof max === 'number' ? max : min;
          recharge = { text: `Recharge ${minNum}-${maxNum}`, min: minNum, max: maxNum };
        } else if (typeof val === 'string') {
          const cleaned = val.replace(/[\u2010-\u2015\u2212]/g, '-');
          const m = cleaned.match(/(\d)\s*-\s*(\d)/);
          if (m) {
            const minNum = parseInt(m[1], 10);
            const maxNum = parseInt(m[2], 10);
            recharge = { text: `Recharge ${minNum}-${maxNum}`, min: minNum, max: maxNum };
          } else {
            const single = cleaned.match(/(\d)/);
            if (single) {
              const n = parseInt(single[1], 10);
              recharge = { text: `Recharge ${n}`, min: n, max: n };
            }
          }
        } else if (typeof val === 'number') {
          const n = Number(val);
          recharge = { text: `Recharge ${n}`, min: n, max: n };
        }
      }
      // date_of_change: 2025-08-25 — consider common top-level variants
      if (!recharge) {
        // Direct min/max
        if (typeof action.rechargeMin === 'number' || typeof action.rechargeMax === 'number') {
          const minNum = typeof action.rechargeMin === 'number' ? action.rechargeMin : action.rechargeMax;
          const maxNum = typeof action.rechargeMax === 'number' ? action.rechargeMax : action.rechargeMin;
          recharge = { text: `Recharge ${minNum}-${maxNum}`, min: minNum, max: maxNum };
        }
      }
      if (!recharge) {
        const altStr = action.rechargeOn || action.rechargeRange || action.rechargeRoll || action.rechargeText;
        if (altStr) {
          const cleaned = String(altStr).replace(/[\u2010-\u2015\u2212]/g, '-');
          const m = cleaned.match(/(\d)\s*-\s*(\d)/);
          if (m) {
            const minNum = parseInt(m[1], 10);
            const maxNum = parseInt(m[2], 10);
            recharge = { text: `Recharge ${minNum}-${maxNum}`, min: minNum, max: maxNum };
          } else {
            const single = cleaned.match(/(\d)/);
            if (single) {
              const n = parseInt(single[1], 10);
              recharge = { text: `Recharge ${n}`, min: n, max: n };
            }
          }
        }
      }
      if (!recharge && action.usage && typeof action.usage === 'object') {
        // 5eTools-like usage object: { type: 'recharge on roll', min: 5, max: 6 } or { type: 'recharge 5-6' }
        const u = action.usage;
        if (/recharge/i.test(String(u.type || ''))) {
          const minNum = (typeof u.min === 'number') ? u.min : undefined;
          const maxNum = (typeof u.max === 'number') ? u.max : undefined;
          if (minNum || maxNum) {
            const a = (typeof minNum === 'number') ? minNum : maxNum;
            const b = (typeof maxNum === 'number') ? maxNum : minNum;
            recharge = { text: `Recharge ${a}-${b}`, min: a, max: b };
          } else if (u.type) {
            const cleaned = String(u.type).replace(/[\u2010-\u2015\u2212]/g, '-');
            const m = cleaned.match(/(\d)\s*-\s*(\d)/);
            if (m) {
              const a = parseInt(m[1], 10);
              const b = parseInt(m[2], 10);
              recharge = { text: `Recharge ${a}-${b}`, min: a, max: b };
            } else {
              const single = cleaned.match(/(\d)/);
              if (single) {
                const n = parseInt(single[1], 10);
                recharge = { text: `Recharge ${n}`, min: n, max: n };
              }
            }
          }
        }
      }
      if (!recharge) {
        recharge = this.parseRechargeFromText(`${name} ${parsedText}`);
      }

      // date_of_change: 2025-08-26
      // - Added parsing of area effects (cone/line/radius/emanation/aura) and targets from description.
      // - For save-based effects (presence of savingThrow), set attackBonus to 0 (N/A) and adjust attackType.
      // - Populate structured fields: areaType, areaSize, areaUnit, areaTargets for downstream UI usage.
      const isBreathWeapon = /breath/i.test(name) || /\b(exhales?|breath\s+weapon)\b/i.test(description || '');
      if (areaInfo) {
        // If we detect an area template, treat this as an area/save type if no explicit attack type provided
        if (!action.type || action.type === 'melee' || action.type === 'ranged') {
          attackType = savingThrow ? 'save' : 'area';
        }
        // Default targets phrase for area actions when not explicitly set
        if (!targets || targets === 'one target') {
          targets = 'each creature in that area';
        }
      }
      
      // For save-based effects, zero out attack bonus and ensure attackType reflects save-based action
      if (savingThrow) {
        attackBonus = 0;
        if (!areaInfo) {
          attackType = 'save';
        }
      }
      // Apply breath-weapon specific defaults
      if (isBreathWeapon) {
        attackType = 'save';
        attackBonus = 0;
        if (areaInfo && (!targets || targets === 'one target')) {
          targets = 'each creature in that area';
        }
      }
      
      // Derive targets from description if generic
      if (!action.targets && typeof description === 'string') {
        const parsedTargets = this.parseTargetsFromText(description);
        if (parsedTargets) targets = parsedTargets;
      }
      
      // If no description but has attack data, generate one
      if (!description && (action.attack || damageEntries.length > 0 || attackBonus)) {
        if (action.attack) {
          description = this.generateAttackDescription(action.attack);
        } else {
          // Build description from discrete fields
          description = this.buildAttackDescription({
            name,
            attackType,
            attackBonus,
            reach: reachOrRange.reach,
            range: reachOrRange.range,
            targets,
            damageEntries,
            savingThrow,
            additionalEffects
          });
        }
      }
      
      // If still no description, provide a default
      if (!description) {
        if (action.effect) {
          description = action.effect;
        } else {
          description = 'No description available.';
        }
      }
      
      // DEBUG
      console.debug(`[MonsterDataNormalizer] Normalized action "${name}":`, {
        damageEntries,
        reachOrRange,
        attackBonus,
        attackType
      });
      
      // Return normalized action with both human-readable description and discrete fields
      return {
        name,
        description,
        
        // Attack properties in discrete fields
        attackType: attackType, // melee, ranged, meleeSpell, rangedSpell
        attackBonus: Number(attackBonus) || 0,
        
        // Distance properties
        reach: reachOrRange.reach || 0,
        range: reachOrRange.range || null,
        targets,
        
        // date_of_change: 2025-08-26
        // Area-of-effect structured fields derived from description
        areaType: areaInfo?.type || null,        // cone | line | sphere | radius | cube | cylinder | aura | burst | emanation
        areaSize: areaInfo?.size || null,        // numeric size (feet)
        areaUnit: areaInfo ? (areaInfo.unit || 'ft') : null, // unit, defaults to ft
        areaTargets: areaInfo?.targets || null,  // text summary (e.g., "each creature")
        
        // Damage entries as structured array for multiple damage types
        damageEntries: damageEntries,
        
        // Saving throw data
        savingThrow,
        
        // Recharge information if present (e.g., "Recharge 5–6")
        rechargeText: recharge?.text || null,
        rechargeMin: recharge?.min ?? null,
        rechargeMax: recharge?.max ?? null,

        // Additional effects text
        additionalEffects,
        
        // Legacy compatibility
        damage: damageEntries.length > 0 ? damageEntries[0].formula : '',
        damageType: damageEntries.length > 0 ? damageEntries[0].type : '',
        type: attackType,

        // Preserve explicit execution metadata verbatim. Runtime code must
        // never infer a resolver from this action's name or description.
        // rule_reference: AGENTS.md no heuristic inference at runtime.
        // date_of_change: 2026-09-02
        actionType: action.actionType,
        activationType: action.activationType,
        standardActionKey: action.standardActionKey,
        executionRoute: action.executionRoute,
        targetRequirement: action.targetRequirement,
        ruleReference: action.ruleReference,
        featureKey: action.featureKey
      };
    });
  }

  /**
   * date_of_change: 2025-08-26
   * Parse area effect information from a freeform action description.
   * Extracts template type (cone/line/sphere/cube/cylinder/radius/aura/burst/emanation),
   * size (feet), unit, and a lightweight targets phrase when present.
   * This is intentionally conservative to avoid false positives.
   */
  static parseAreaEffectFromDescription(description) {
    if (!description || typeof description !== 'string') return null;
    
    // Common area templates and synonyms
    const patterns = [
      { type: 'cone', regex: /(\bcone\b).*?(\d+)\s*-?\s*(?:foot|feet|ft)/i },
      { type: 'line', regex: /(\bline\b).*?(\d+)\s*-?\s*(?:foot|feet|ft)/i },
      // sphere often appears as "20-foot radius". Capture radius specifically
      { type: 'sphere', regex: /(\bradius\b).*?(\d+)\s*-?\s*(?:foot|feet|ft)/i },
      { type: 'cube', regex: /(\bcube\b).*?(\d+)\s*-?\s*(?:foot|feet|ft)/i },
      { type: 'cylinder', regex: /(\bcylinder\b).*?(\d+)\s*-?\s*(?:foot|feet|ft)/i },
      { type: 'aura', regex: /(\baura\b).*?(\d+)\s*-?\s*(?:foot|feet|ft)/i },
      { type: 'emanation', regex: /(\bemanation\b).*?(\d+)\s*-?\s*(?:foot|feet|ft)/i },
      { type: 'burst', regex: /(\bburst\b).*?(\d+)\s*-?\s*(?:foot|feet|ft)/i },
    ];
    
    for (const p of patterns) {
      const m = description.match(p.regex);
      if (m) {
        const size = parseInt(m[2]);
        // Try to capture a simple target phrase nearby
        const targetsMatch = description.match(/(each|every|all)\s+(creature|target|enemy|foe)s?/i);
        return {
          type: p.type,
          size: isNaN(size) ? null : size,
          unit: 'ft',
          targets: targetsMatch ? `${targetsMatch[1]} ${targetsMatch[2]}${targetsMatch[0].endsWith('s') ? 's' : ''}` : null
        };
      }
    }
    
    // Special handling: phrases like "in a 60-foot line" or "in a 30-foot cone"
    const inlineMatch = description.match(/in\s+a\s+(\d+)\s*-?\s*(?:foot|feet|ft)\s+(cone|line|cube|sphere|cylinder)/i);
    if (inlineMatch) {
      const size = parseInt(inlineMatch[1]);
      return {
        type: inlineMatch[2].toLowerCase(),
        size: isNaN(size) ? null : size,
        unit: 'ft',
        targets: null
      };
    }
    
    return null;
  }

  /**
   * date_of_change: 2025-08-26
   * Parse Recharge information from text (name/description).
   * Supports formats like "Recharge 5–6", "Recharge 6", or "(Recharge 5-6)".
   */
  static parseRechargeFromText(text) {
    if (!text || typeof text !== 'string') return null;
    // Normalize hyphen types (en dash/em dash) to ASCII '-' for regex simplicity
    const t = text.replace(/[\u2010-\u2015\u2212]/g, '-');
    // Match ranges like 5-6
    let m = t.match(/recharge\s*(\d)\s*-\s*(\d)/i);
    if (m) {
      const min = parseInt(m[1], 10);
      const max = parseInt(m[2], 10);
      return { text: `Recharge ${min}-${max}`, min, max };
    }
    // Match single value like Recharge 6
    m = t.match(/recharge\s*(\d)/i);
    if (m) {
      const val = parseInt(m[1], 10);
      return { text: `Recharge ${val}`, min: val, max: val };
    }
    return null;
  }
  
  /**
   * date_of_change: 2025-08-26
   * Parse a human-readable targets phrase from text, e.g.,
   * - "each creature in the area"
   * - "up to three creatures"
   * - "one target"
   */
  static parseTargetsFromText(description) {
    if (!description || typeof description !== 'string') return null;
    // Priority: explicit counts
    const upToCount = description.match(/up to\s+(\w+)\s+(creatures|targets)/i);
    if (upToCount) return `up to ${upToCount[1]} ${upToCount[2]}`;
    const oneTarget = description.match(/\bone\b\s+(target|creature)/i);
    if (oneTarget) return `one ${oneTarget[1]}`;
    const eachCreat = description.match(/(each|every|all)\s+(creature|target|enemy|foe)s?/i);
    if (eachCreat) return `${eachCreat[1]} ${eachCreat[2]}${eachCreat[0].endsWith('s') ? 's' : ''}`;
    return null;
  }
  
    /**
   * Extract attack bonus from action description or properties
   * 
   * @param {string|Object} input - Action description string or action object
   * @returns {number} Extracted attack bonus as a number
   */
  static extractAttackBonus(input) {
    // DEBUG
    console.debug('[MonsterDataNormalizer] Extracting attack bonus from:', input);
    
    // If input is an object with attackBonus property, use that
    if (input && typeof input === 'object') {
      if (input.attackBonus !== undefined) {
        return Number(input.attackBonus) || 0;
      }
      
      if (input.attack && input.attack.toHit !== undefined) {
        return Number(input.attack.toHit) || 0;
      }
      
      // Try to extract from description
      if (input.description) {
        return this.extractAttackBonus(input.description);
      }
    }
    
    // If input is a string, try to extract from text
    if (typeof input === 'string') {
      // Look for pattern like "+5 to hit" or "+8 to hit"
      const match = input.match(/([+-]\d+)\s+to\s+hit/i);
      if (match) {
        return parseInt(match[1]);
      }
    }
    
    return 0;
  }

  /**
   * Extract reach and range information from action data
   * 
   * @param {Object} action - Action object with potential reach/range properties
   * @returns {Object} Object containing normalized reach and range values
   */
  static extractReachRange(action) {
    console.debug('[MonsterDataNormalizer] Extracting reach/range from:', JSON.stringify(action, null, 2));
    
    // Initialize result object
    const result = {
      reach: 0,
      range: null
    };

    // Extract reach from different possible formats
    if (action.reach) {
      result.reach = Number(action.reach) || 5;
    } else if (action.attack?.reach) {
      result.reach = Number(action.attack.reach) || 5;
    } else if (typeof action.description === 'string') {
      // Try to extract reach from description
      const reachMatch = action.description.match(/reach\s+(\d+)\s*ft/i);
      if (reachMatch) {
        result.reach = parseInt(reachMatch[1]);
      } else {
        // Default melee reach is 5 ft
        const isRanged = action.attackType === 'ranged' || 
                       action.type === 'ranged' || 
                       action.type === 'rangedWeapon' || 
                       action.type === 'rangedSpell' ||
                       action.description?.toLowerCase().includes('ranged');
        result.reach = isRanged ? 0 : 5;
      }
    } else {
      // Default melee reach is 5 ft
      const isRanged = action.attackType === 'ranged' || 
                      action.type === 'ranged' || 
                      action.type === 'rangedWeapon' || 
                      action.type === 'rangedSpell';
      result.reach = isRanged ? 0 : 5;
    }

    // Extract range from different possible formats
    if (action.range) {
      if (typeof action.range === 'object') {
        result.range = {
          normal: Number(action.range.normal) || 0,
          long: Number(action.range.long) || 0
        };
      } else {
        result.range = {
          normal: Number(action.range) || 0,
          long: 0
        };
      }
    } else if (action.attack?.range) {
      if (typeof action.attack.range === 'object') {
        result.range = {
          normal: Number(action.attack.range.normal) || 0,
          long: Number(action.attack.range.long) || 0
        };
      } else {
        result.range = {
          normal: Number(action.attack.range) || 0,
          long: 0
        };
      }
    } else if (typeof action.description === 'string') {
      // Try to extract range from description like "range 150/600 ft."
      const rangeMatch = action.description.match(/range\s+(\d+)(?:\/(\d+))?\s*ft/i);
      if (rangeMatch) {
        result.range = {
          normal: parseInt(rangeMatch[1]),
          long: rangeMatch[2] ? parseInt(rangeMatch[2]) : 0
        };
      }
    }

    return result;
  }

  /**
   * Extract damage entries from action data supporting multiple damage types
   * 
   * @param {Object} action - Action object with potential damage information
   * @returns {Array} Array of normalized damage entry objects
   * 
   * @example
   * // Returns [{type: "slashing", dice: "1d8", bonus: 4, formula: "1d8+4", average: 8.5}]
   */
  static extractDamageEntries(action) {
    console.debug('[MonsterDataNormalizer] Extracting damage entries from:', JSON.stringify(action, null, 2));
    const damageEntries = [];

    // Case 1: Action has an attack object with damage array
    if (action.attack?.damage && Array.isArray(action.attack.damage)) {
      console.debug('[MonsterDataNormalizer] Found attack.damage array:', action.attack.damage);
      action.attack.damage.forEach(dmg => {
        const entry = this.parseDamageEntry(dmg);
        if (entry) damageEntries.push(entry);
      });
    }
    
    // Case 2: Legacy format with damage and damageType properties
    else if (action.damage && (action.damageType || action.type)) {
      console.debug('[MonsterDataNormalizer] Found legacy damage format:', action.damage, action.damageType || action.type);
      // Handle comma-separated multiple damage types and formulas
      if (typeof action.damage === 'string' && action.damage.includes(',')) {
        // Split both damage formula and damage types if they contain commas
        const damageFormulas = action.damage.split(',').map(f => f.trim());
        let damageTypes = [];
        
        if (typeof action.damageType === 'string' && action.damageType.includes(',')) {
          damageTypes = action.damageType.split(',').map(t => t.trim());
        } else {
          // If we have multiple formulas but only one damage type, use it for all
          damageTypes = Array(damageFormulas.length).fill(action.damageType || 'bludgeoning');
        }
        
        // Create an entry for each formula/type pair
        damageFormulas.forEach((formula, i) => {
          const type = damageTypes[i] || damageTypes[0] || 'bludgeoning';
          const entry = this.parseDamageFormula(formula, type);
          if (entry) damageEntries.push(entry);
        });
      } else {
        // Single damage formula and type
        const damageFormula = action.damage;
        const damageType = action.damageType || action.type || 'bludgeoning'; 
        
        const entry = this.parseDamageFormula(damageFormula, damageType);
        if (entry) damageEntries.push(entry);
      }
    }
    
    // Case 3: Parse from description if we have no structured damage data
    else if (action.description && typeof action.description === 'string') {
      console.debug('[MonsterDataNormalizer] Trying to parse damage from description:', action.description);
      
      // Look for patterns like "10 (2d6 + 3) piercing damage" or similar
      const damagePattern = /(\d+)\s*\((\d+d\d+(?:\s*[+-]\s*\d+)?)\)\s*([a-z]+)\s*damage/gi;
      let match;
      
      while ((match = damagePattern.exec(action.description)) !== null) {
        const formula = match[2].replace(/\s+/g, '');
        const type = match[3].toLowerCase();
        console.debug(`[MonsterDataNormalizer] Found damage in description: ${formula} ${type}`);
        
        const entry = this.parseDamageFormula(formula, type);
        if (entry) damageEntries.push(entry);
      }
    }
    
    // If no damage entries were successfully parsed but we have a damage property,
    // create a default entry
    if (damageEntries.length === 0 && action.damage) {
      console.debug('[MonsterDataNormalizer] Creating default damage entry from:', action.damage);
      const entry = this.parseDamageFormula(action.damage, action.damageType || 'bludgeoning');
      if (entry) damageEntries.push(entry);
    }

    console.debug('[MonsterDataNormalizer] Extracted damage entries:', JSON.stringify(damageEntries, null, 2));
    return damageEntries;
  }
  
  /**
   * Parse a damage formula into a structured object
   * 
   * @param {string} formula - Damage formula string (e.g., "1d8+4")
   * @param {string} type - Damage type (e.g., "slashing")
   * @returns {Object|null} Parsed damage entry or null if invalid
   */
  static parseDamageFormula(formula, type = 'bludgeoning') {
    if (!formula) return null;
    
    // Parse dice formula like "2d6+3"
    const match = String(formula).match(/(\d+)d(\d+)(?:([+-])(\d+))?/);
    
    if (match) {
      const numDice = parseInt(match[1]);
      const diceSize = parseInt(match[2]);
      const hasModifier = match[3] !== undefined;
      const modifier = hasModifier ? parseInt(match[4]) : 0;
      const modSign = hasModifier && match[3] === '-' ? -1 : 1;
      const bonus = hasModifier ? modifier * modSign : 0;
      
      // Calculate average damage
      const average = (numDice * (diceSize + 1) / 2) + bonus;
      
      return {
        type: type.toLowerCase(),
        dice: `${numDice}d${diceSize}`,
        bonus: bonus,
        formula: formula,
        average: average
      };
    } 
    // Try to parse as just a number
    else if (!isNaN(formula)) {
      const damage = parseInt(formula);
      return {
        type: type.toLowerCase(),
        dice: '',
        bonus: damage,
        formula: String(formula),
        average: damage
      };
    }
    
    // Couldn't parse, return default
    return {
      type: type.toLowerCase(),
      dice: '',
      bonus: 0,
      formula: String(formula),
      average: 0
    };
  }
  
  /**
   * Parse damage entry object into a standardized format
   * 
   * @param {Object} dmg - Damage object from various formats
   * @returns {Object|null} Standardized damage entry object or null if invalid
   */
  static parseDamageEntry(dmg) {
    if (!dmg) return null;
    
    // Handle legacy damage objects
    if (dmg.dice || dmg.formula) {
      const formula = dmg.dice || dmg.formula;
      const match = formula.match(/(\d+)d(\d+)(?:([+-])(\d+))?/);
      
      let dice = '';
      let bonus = 0;
      
      if (match) {
        dice = `${match[1]}d${match[2]}`;
        if (match[3]) {
          const modSign = match[3] === '-' ? -1 : 1;
          bonus = parseInt(match[4]) * modSign;
        }
      }
      
      return {
        type: dmg.type || 'bludgeoning',
        dice: dice,
        bonus: bonus,
        formula: formula,
        average: dmg.avg || this.calculateAverageDamage(formula)
      };
    }
    
    // If there's only damage value but no formula, use that
    if (dmg.value !== undefined) {
      return {
        type: dmg.type || 'bludgeoning',
        dice: '',
        bonus: Number(dmg.value),
        formula: String(dmg.value),
        average: Number(dmg.value)
      };
    }
    
    // If we can't parse it, return null
    return null;
  }

  /**
   * Extract saving throw information from action data
   * 
   * @param {Object} action - Action object with potential saving throw information
   * @returns {Object|null} Saving throw object or null if none exists
   */
  static extractSavingThrow(action) {
    console.debug('[MonsterDataNormalizer] Extracting saving throw from:', JSON.stringify(action, null, 2));
    
    // Case 1: Action has a save property in its attack object
    if (action.attack?.save) {
      const save = action.attack.save;
      const result = {
        ability: save.ability || 'str',
        dc: save.dc || 10,
        notes: save.notes || '',
        success: save.success || 'half',  // half, none, etc.
        failEffect: save.failEffect || ''
      };
      console.debug('[MonsterDataNormalizer] Found saving throw in attack.save:', JSON.stringify(result, null, 2));
      return result;
    }
    
    // Case 2: Action has direct save properties
    else if (action.saveDC && action.saveAbility) {
      const result = {
        ability: action.saveAbility.toLowerCase(),
        dc: Number(action.saveDC),
        notes: action.saveNotes || '',
        success: action.saveSuccess || 'half',
        failEffect: action.saveFailEffect || ''
      };
      console.debug('[MonsterDataNormalizer] Found saving throw in direct properties:', JSON.stringify(result, null, 2));
      return result;
    }
    
    // Case 3: Try to parse from description
    else if (action.description && typeof action.description === 'string') {
      // Look for DC pattern like "DC 15 Dexterity saving throw"
      const saveMatch = action.description.match(/DC\s+(\d+)\s+([a-zA-Z]+)(?:\s+saving\s+throw)/i);
      if (saveMatch) {
        const dc = parseInt(saveMatch[1]);
        let ability = saveMatch[2].toLowerCase();
        
        // Map full ability name to abbreviation
        const abilityMap = {
          'strength': 'str',
          'dexterity': 'dex',
          'constitution': 'con',
          'intelligence': 'int',
          'wisdom': 'wis',
          'charisma': 'cha'
        };
        
        ability = abilityMap[ability] || ability.substring(0, 3).toLowerCase();
        
        // date_of_change: 2025-08-26
        // Determine success semantics and parse notes/failure clauses from prose.
        let success = 'none';
        const lower = action.description.toLowerCase();
        if (lower.includes('half as much damage on a successful')) {
          success = 'half';
        }

        // Start building notes and failure effect from common patterns
        let notes = '';
        let failEffect = '';

        // Capture repeat-save phrasing (e.g., can repeat the saving throw at the end of each of its turns ...)
        const repeatMatch = action.description.match(/can repeat the saving throw[^.]*\./i);
        if (repeatMatch) {
          notes += (notes ? ' ' : '') + repeatMatch[0].trim();
        }

        // Capture immunity on success for 24 hours (various phrasings)
        const immune24Match = action.description.match(/immune[^.]*?24\s*hours?/i);
        if (immune24Match) {
          // Normalize to a consistent note for downstream consumers
          notes += (notes ? ' ' : '') + 'On success, the creature becomes immune to this effect for 24 hours.';
        }

        // Capture explicit success clause text if present (without half-damage which we already model)
        const successClause = action.description.match(/on a success[,;:]?\s*(.*?)(?:\.|$)/i);
        if (successClause && successClause[1] && !/half as much damage/i.test(successClause[1])) {
          notes += (notes ? ' ' : '') + `On success: ${successClause[1].trim()}.`;
        }

        // Capture failure effect clause
        const failClause = action.description.match(/on a failed save[,;:]?\s*(.*?)(?:\.|$)/i) ||
                           action.description.match(/on failure[,;:]?\s*(.*?)(?:\.|$)/i);
        if (failClause && failClause[1]) {
          failEffect = failClause[1].trim();
        } else {
          // Look for common conditions with durations, e.g., frightened for 1 minute
          const conditionDuration = action.description.match(/\b(bleeding|blinded|charmed|deafened|frightened|grappled|incapacitated|paralyzed|petrified|poisoned|prone|restrained|stunned|unconscious)\b[^.]*?for\s+(\d+)\s*(rounds?|minutes?|hours?)/i);
          if (conditionDuration) {
            failEffect = `${conditionDuration[1]} for ${conditionDuration[2]} ${conditionDuration[3]}`;
          }
        }

        const result = {
          ability: ability,
          dc: dc,
          notes: notes,
          success: success,
          failEffect: failEffect
        };
        
        console.debug('[MonsterDataNormalizer] Extracted saving throw from description:', JSON.stringify(result, null, 2));
        return result;
      }
    }
    
    console.debug('[MonsterDataNormalizer] No saving throw found');
    return null;
  }
  
  /**
   * Build a descriptive attack string from structured data
   * 
   * @param {Object} attack - Structured attack data
   * @returns {string} Human-readable attack description
   */
  static buildAttackDescription(attack) {
    if (!attack) return '';
    
    let desc = '';
    
    // Attack type
    const attackTypeMap = {
      'melee': 'Melee Weapon Attack',
      'meleeWeapon': 'Melee Weapon Attack',
      'meleeSpell': 'Melee Spell Attack',
      'ranged': 'Ranged Weapon Attack',
      'rangedWeapon': 'Ranged Weapon Attack',
      'rangedSpell': 'Ranged Spell Attack',
      'area': 'Area Attack',
      'save': 'Saving Throw Attack'
    };
    
    const attackTypeText = attackTypeMap[attack.attackType] || 'Attack';
    desc += `${attackTypeText}: `;
    
    // Attack bonus
    if (attack.attackBonus) {
      desc += `+${attack.attackBonus} to hit, `;
    }
    
    // Reach and Range
    if (attack.reach) {
      desc += `reach ${attack.reach} ft., `;
    }
    if (attack.range) {
      if (attack.range.long > 0) {
        desc += `range ${attack.range.normal}/${attack.range.long} ft., `;
      } else {
        desc += `range ${attack.range.normal} ft., `;
      }
    }
    
    // Target
    desc += `${attack.targets || 'one target'}. `;
    
    // Damage
    if (attack.damageEntries && attack.damageEntries.length > 0) {
      desc += 'Hit: ';
      
      // Handle multiple damage types
      attack.damageEntries.forEach((entry, index) => {
        if (index > 0) desc += ' plus ';
        
        desc += `${Math.floor(entry.average)} (${entry.formula}) ${entry.type} damage`;
      });
      
      desc += '.';
    }
    
    // Saving throw
    if (attack.savingThrow) {
      const save = attack.savingThrow;
      const abilityNames = {
        'str': 'Strength',
        'dex': 'Dexterity',
        'con': 'Constitution',
        'int': 'Intelligence',
        'wis': 'Wisdom',
        'cha': 'Charisma'
      };
      
      desc += ` Target must make a DC ${save.dc} ${abilityNames[save.ability] || save.ability} saving throw.`;
      
      if (save.failEffect) {
        desc += ` Failure: ${save.failEffect}.`;
      }
      
      if (save.success === 'half') {
        desc += ` Success: half damage.`;
      } else if (save.success === 'none') {
        desc += ` Success: no damage.`;
      } else if (save.success) {
        desc += ` Success: ${save.success}.`;
      }
      
      if (save.notes) {
        desc += ` ${save.notes}`;
      }
    }
    
    // Additional effects
    if (attack.additionalEffects) {
      desc += ` ${attack.additionalEffects}`;
    }
    
    return desc;
  }
  
  /**
   * Generate attack description from attack object (legacy format)
   */
  static generateAttackDescription(attack) {
    if (!attack) return '';
    
    let desc = '';
    
    // Attack type and bonus
    if (attack.type) {
      const attackType = attack.type === 'meleeWeapon' ? 'Melee Weapon Attack' : 
                        attack.type === 'rangedWeapon' ? 'Ranged Weapon Attack' :
                        attack.type === 'meleeSpell' ? 'Melee Spell Attack' :
                        attack.type === 'rangedSpell' ? 'Ranged Spell Attack' : 'Attack';
      desc += `${attackType}: `;
    }
    
    if (attack.toHit) {
      desc += `+${attack.toHit} to hit, `;
    }
    
    // Range/Reach
    if (attack.reach) {
      desc += `reach ${attack.reach} ft., `;
    }
    if (attack.range) {
      if (typeof attack.range === 'object') {
        desc += `range ${attack.range.normal}/${attack.range.long} ft., `;
      } else {
        desc += `range ${attack.range} ft., `;
      }
    }
    
    // Target
    desc += `${attack.targets || 'one target'}. `;
    
    // Damage
    if (attack.damage && attack.damage.length > 0) {
      const dmg = attack.damage[0];
      const avgDamage = dmg.avg || this.calculateAverageDamage(dmg.dice || dmg.formula || '1d6');
      const damageFormula = dmg.dice || dmg.formula || '1d6';
      desc += `Hit: ${avgDamage} (${damageFormula}) ${dmg.type} damage.`;
      
      // Handle additional damage types
      for (let i = 1; i < attack.damage.length; i++) {
        const additionalDmg = attack.damage[i];
        const addAvgDmg = additionalDmg.avg || this.calculateAverageDamage(additionalDmg.dice || additionalDmg.formula || '1d6');
        const addFormula = additionalDmg.dice || additionalDmg.formula || '1d6';
        desc += ` Plus ${addAvgDmg} (${addFormula}) ${additionalDmg.type} damage.`;
      }
    }
    
    // Additional effects
    if (attack.riders) desc += ` ${attack.riders}`;
    if (attack.notes) desc += ` ${attack.notes}`;
    
    // Saving throw
    if (attack.save) {
      const save = attack.save;
      desc += ` Target must make a DC ${save.dc} ${save.ability} saving throw.`;
      if (save.notes) desc += ` ${save.notes}.`;
    }
    
    return desc;
  }
  
  /**
   * Calculate average damage from dice formula
   */
  static calculateAverageDamage(diceFormula) {
    if (!diceFormula) return 0;
    
    // Handle simple numbers
    if (!isNaN(diceFormula)) return parseInt(diceFormula);
    
    // Parse dice formulas like "1d6+2", "2d4+2", etc.
    const match = diceFormula.match(/(\d+)d(\d+)(?:([+-])(\d+))?/i);
    if (match) {
      const numDice = parseInt(match[1]);
      const dieSize = parseInt(match[2]);
      const modSign = match[3] === '-' ? -1 : 1;
      const modifier = match[4] ? parseInt(match[4]) : 0;
      return Math.floor(numDice * (dieSize + 1) / 2) + (modifier * modSign);
    }
    
    return 0;
  }

  /**
   * Extract lore data
   */
  static extractLore(data) {
    if (data.lore && typeof data.lore === 'object') return data.lore;
    
    // Try to extract from traits
    const lore = {};
    const traits = data.traits || data.data?.traits || [];
    
    traits.forEach(trait => {
      if (trait.name === 'Lore') lore.description = trait.description || trait.text;
      if (trait.name === 'Habitat') lore.habitat = trait.description || trait.text;
      if (trait.name === 'Tactics') lore.tactics = trait.description || trait.text;
    });
    
    return lore;
  }

  /**
   * Get default monster template
   */
  static getDefaultMonster() {
    return {
      id: 'unknown',
      name: 'Unknown Monster',
      type: 'monster',
      cr: '1/4',
      hp: 10,
      maxHp: 10,
      ac: 10,
      speed: 30,
      initiative: 0,
      size: 'Medium',
      monsterType: 'humanoid',
      alignment: 'neutral',
      stats: { str: 10, dex: 10, con: 10, int: 10, wis: 10, cha: 10 },
      abilities: { strength: 10, dexterity: 10, constitution: 10, intelligence: 10, wisdom: 10, charisma: 10 },
      skills: {},
      savingThrows: {},
      damageResistances: [],
      damageImmunities: [],
      damageVulnerabilities: [],
      conditionImmunities: [],
      senses: 'passive Perception 10',
      languages: 'Common',
      actions: [],
      bonusActions: [],
      specialAbilities: [],
      traits: [],
      legendaryActions: [],
      reactions: [],
      artwork: '/assets/monsters/default-monster.png',
      token: '/assets/monsters/tokens/default-token.png',
      lore: {},
      isCustom: false,
      source: { name: 'Unknown', edition: '2024 Core' }
    };
  }

  /**
   * Calculate XP value based on Challenge Rating
   * 
   * @param {string|number} cr - Challenge rating as string or number
   * @returns {number} - XP value
   */
  static calculateXPFromCR(cr) {
    // Handle string CR values with fractions like "1/4", "1/2" etc.
    let numericCR = cr;
    if (typeof cr === 'string') {
      // Handle fraction format
      if (cr.includes('/')) {
        const parts = cr.split('/');
        numericCR = parseInt(parts[0]) / parseInt(parts[1]);
      } else {
        // Handle numeric strings
        numericCR = parseFloat(cr);
      }
    }
    
    // Standard 5e XP values by CR
    const crXPTable = {
      '0': 10,
      '1/8': 25,
      0.125: 25,
      '1/4': 50,
      0.25: 50,
      '1/2': 100,
      0.5: 100,
      1: 200,
      2: 450,
      3: 700,
      4: 1100,
      5: 1800,
      6: 2300,
      7: 2900,
      8: 3900,
      9: 5000,
      10: 5900,
      11: 7200,
      12: 8400,
      13: 10000,
      14: 11500,
      15: 13000,
      16: 15000,
      17: 18000,
      18: 20000,
      19: 22000,
      20: 25000,
      21: 33000,
      22: 41000,
      23: 50000,
      24: 62000,
      25: 75000,
      26: 90000,
      27: 105000,
      28: 120000,
      29: 135000,
      30: 155000
    };
    
    // Look up exact CR match
    if (crXPTable[numericCR] !== undefined) {
      return crXPTable[numericCR];
    }
    
    // For non-standard CR values, find nearest match
    const crValues = Object.keys(crXPTable)
      .filter(key => !isNaN(parseFloat(key)))
      .map(key => parseFloat(key))
      .sort((a, b) => a - b);
    
    // Find nearest CR value
    let nearest = crValues[0];
    for (const val of crValues) {
      if (Math.abs(val - numericCR) < Math.abs(nearest - numericCR)) {
        nearest = val;
      }
    }
    
    console.debug(`[DEBUG:XP] Calculated XP for CR ${cr} using nearest CR ${nearest}: ${crXPTable[nearest]}`);
    return crXPTable[nearest] || 0;
  }

  /**
   * Validate normalized monster data
   */
  static validate(monster) {
    const required = ['id', 'name', 'type', 'cr', 'hp', 'ac'];
    const missing = required.filter(field => !monster[field]);
    
    if (missing.length > 0) {
      console.warn(`[MonsterDataNormalizer] Missing required fields: ${missing.join(', ')}`);
    }
    
    // Validate stats - ensure stats object exists before validation
    let invalidStats = [];
    if (monster.stats) {
      const statNames = ['str', 'dex', 'con', 'int', 'wis', 'cha'];
      invalidStats = statNames.filter(stat => 
        typeof monster.stats[stat] !== 'number' || monster.stats[stat] < 1 || monster.stats[stat] > 30
      );
      
      if (invalidStats.length > 0) {
        console.warn(`[MonsterDataNormalizer] Invalid stats: ${invalidStats.join(', ')}`);
      }
    } else {
      // Create stats from abilities if missing
      monster.stats = {
        str: monster.abilities?.strength || 10,
        dex: monster.abilities?.dexterity || 10,
        con: monster.abilities?.constitution || 10,
        int: monster.abilities?.intelligence || 10,
        wis: monster.abilities?.wisdom || 10,
        cha: monster.abilities?.charisma || 10
      };
      console.warn('[MonsterDataNormalizer] Created stats from abilities');
    }
    
    return missing.length === 0 && invalidStats.length === 0;
  }
}

export default MonsterDataNormalizer;
